(function () {

  const DEBUG              = false;
  const NEAR_END_THRESHOLD = 4 * 60;
  const MIN_POPUP_SECONDS  = 60;
  const RESUME_BACK_OFFSET = 10;
  const COOKIE_EXPIRY_DAYS = 60;
  const COOKIE_PREFIX      = 'vimeo_progress_';
  const MIN_SAVE_SECONDS   = 10;
  const POPUP_DELAY_MS     = 1500;
  const NETLIFY_ENDPOINT   = 'https://astounding-cassata-06e113.netlify.app/.netlify/functions/mark-watched';
  const MAX_RETRIES        = 5;
  const RETRY_DELAYS       = [3000, 5000, 10000, 20000, 30000];

  function log(...args)  { if (DEBUG) console.log('[Vimeo]', ...args); }
  function warn(...args) { if (DEBUG) console.warn('[Vimeo]', ...args); }

  function formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  function getCookie(name) {
    const match = document.cookie.split('; ').find(row => row.startsWith(name + '='));
    return match ? decodeURIComponent(match.split('=')[1]) : null;
  }

  function deleteCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  function getVideoId(iframe) {
    const match = iframe.src.match(/vimeo\.com\/video\/(\d+)/);
    return match ? match[1] : 'unknown';
  }

  // ─── Init ───────────────────────────────────────────────────────────────────

  const iframe = document.querySelector('iframe[src*="player.vimeo.com"]');
  if (!iframe) return;

  const player     = new Vimeo.Player(iframe);
  const videoId    = getVideoId(iframe);
  const cookieName = COOKIE_PREFIX + videoId;

  const customerId  = window.SHOPIFY_CUSTOMER_ID  || null;
  const videoHandle = window.SHOPIFY_VIDEO_HANDLE || '';
  const videoTitle  = window.SHOPIFY_VIDEO_TITLE  || '';
  const videoImage  = window.SHOPIFY_VIDEO_IMAGE  || '';

  let currentSeconds    = 0;
  let watchedMarkSent   = false;
  let watchedRetryCount = 0;

  // ─── Progress cookie ────────────────────────────────────────────────────────

  function saveProgress() {
    if (currentSeconds < MIN_SAVE_SECONDS) return;
    const data = JSON.stringify({ s: parseFloat(currentSeconds.toFixed(2)), h: videoHandle || '', t: videoTitle, i: videoImage });
    setCookie(cookieName, data, COOKIE_EXPIRY_DAYS);
    log(`🍪 Progression sauvegardée : ${formatTime(currentSeconds)}`);
  }

  // ─── Mark as watched (Netlify) ──────────────────────────────────────────────

  async function markVideoAsWatched() {
    if (watchedMarkSent || !customerId || !videoHandle) return;

    watchedMarkSent = true;
    log(`📡 Marquage — customer ${customerId} / "${videoHandle}"`);

    try {
      const res  = await fetch(NETLIFY_ENDPOINT, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ customerId, videoHandle }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.status);
      watchedRetryCount = 0;
      log(`✅ "${videoHandle}" marquée comme vue.`);
    } catch (err) {
      warn('❌ Erreur marquage :', err.message);
      if (watchedRetryCount < MAX_RETRIES) {
        const delay = RETRY_DELAYS[watchedRetryCount++];
        watchedMarkSent = false;
        setTimeout(markVideoAsWatched, delay);
      }
    }
  }

  // ─── Resume popup ───────────────────────────────────────────────────────────

  let resumeTimeout = null;

  function hidePopup() {
    document.getElementById('vimeo-resume-popup')?.classList.add('inactive');
  }

  function showResumePopup(savedTime) {
    const resumeAt  = Math.max(0, savedTime - RESUME_BACK_OFFSET);
    const popup     = document.getElementById('vimeo-resume-popup');
    const timeLabel = document.getElementById('vimeo-resume-time');
    const yesBtn    = document.getElementById('vimeo-resume-yes');
    const noBtn     = document.getElementById('vimeo-resume-no');

    if (!popup || !timeLabel || !yesBtn || !noBtn) return;

    timeLabel.textContent = formatTime(resumeAt);
    popup.classList.remove('inactive');

    yesBtn.onclick = () => {
      hidePopup();
      deleteCookie(cookieName);
      player.setCurrentTime(resumeAt).then(() => player.play());
    };

    noBtn.onclick = () => {
      hidePopup();
      deleteCookie(cookieName);
      player.setCurrentTime(0);
    };
  }

  // ─── Player events ──────────────────────────────────────────────────────────

  player.ready().then(async () => {
    const duration = await player.getDuration();
    const saved    = getCookie(cookieName);
    if (!saved) return;

    let savedTime;
    try   { savedTime = JSON.parse(saved).s; }
    catch { savedTime = parseFloat(saved); }  // backward compat with old format

    const remaining = duration - savedTime;

    if (savedTime < MIN_POPUP_SECONDS) {
      return;
    } else if (remaining < NEAR_END_THRESHOLD || savedTime / duration >= 0.98) {
      deleteCookie(cookieName);
    } else {
      resumeTimeout = setTimeout(() => {
        resumeTimeout = null;
        showResumePopup(savedTime);
      }, POPUP_DELAY_MS);
    }
  }).catch(err => warn('❌ Erreur player :', err));

  player.on('timeupdate', ({ seconds, duration }) => {
    currentSeconds = seconds;
    const remaining = duration - seconds;
    if (remaining <= NEAR_END_THRESHOLD && remaining > 0) {
      markVideoAsWatched();
    }
  });

  player.on('play', () => {
    if (resumeTimeout) { clearTimeout(resumeTimeout); resumeTimeout = null; }
    hidePopup();
  });

  player.on('pause',  () => saveProgress());
  player.on('ended',  () => { deleteCookie(cookieName); currentSeconds = 0; markVideoAsWatched(); });
  player.on('error',  (err) => warn('❌ Erreur player :', err));

  function handlePageLeave() { saveProgress(); }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') handlePageLeave();
  });
  window.addEventListener('beforeunload', handlePageLeave);
  window.addEventListener('pagehide',     handlePageLeave);

})();
